import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, urlDeFoto, type User } from '../api/client';
import { AvisoError, AvisoExito } from './Aviso';
import { Modal } from './Modal';

const MAX_FOTO_BYTES = 4 * 1024 * 1024;

/**
 * Poner o quitar la foto de alguien. Es de quien administra —general o local,
 * `user.photo`—: la persona ya no se la pone sola.
 *
 * Se sube en cuanto se elige, sin un «Guardar» aparte: elegir el archivo ya es
 * la decisión, y la vista previa que vuelve es la que se guardó, ya reducida.
 */
export function FotoDePersona({
  usuario,
  onCambio,
  onCerrar,
}: {
  usuario: User;
  onCambio: (photoUpdatedAt: string | null) => void;
  onCerrar: () => void;
}) {
  const { t } = useTranslation();
  const archivo = useRef<HTMLInputElement>(null);
  const [actualizada, setActualizada] = useState<string | null>(usuario.photoUpdatedAt ?? null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [pesada, setPesada] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const foto = urlDeFoto(usuario.id, actualizada);
  const iniciales = usuario.fullName
    .split(/\s+/)
    .slice(0, 2)
    .map((parte) => parte.charAt(0).toUpperCase())
    .join('');

  const cambiar = async (accion: () => Promise<{ photoUpdatedAt: string | null }>, mensaje: string) => {
    setOcupado(true);
    setError(null);
    setPesada(false);
    setAviso(null);
    try {
      const { photoUpdatedAt } = await accion();
      setActualizada(photoUpdatedAt);
      setAviso(mensaje);
      onCambio(photoUpdatedAt);
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  };

  const subir = (elegido: File) => {
    // Se revisa aquí además del servidor: mandar cuatro megas para que los
    // rechacen desperdicia la subida completa en una sucursal con mala conexión.
    if (elegido.size > MAX_FOTO_BYTES) {
      setAviso(null);
      setError(null);
      setPesada(true);
      return;
    }
    void cambiar(() => api.setUserPhoto(usuario.id, elegido), t('photo.saved'));
  };

  return (
    <Modal titulo={t('photo.title', { nombre: usuario.fullName })} onCerrar={onCerrar}>
      {pesada ? (
        <p className="aviso aviso--error" role="alert">
          {t('photo.tooBig')}
        </p>
      ) : (
        <AvisoError error={error} />
      )}
      {aviso && <AvisoExito>{aviso}</AvisoExito>}

      <div className="perfil__foto-fila">
        {foto ? (
          <img className="perfil__foto" src={foto} alt={usuario.fullName} />
        ) : (
          <span className="perfil__iniciales" aria-hidden="true">
            {iniciales}
          </span>
        )}

        <div className="perfil__foto-acciones">
          <input
            ref={archivo}
            type="file"
            accept="image/*"
            className="visualmente-oculto"
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => {
              const elegido = e.target.files?.[0];
              if (elegido) subir(elegido);
              // Se limpia para poder volver a elegir el mismo archivo si la
              // primera vez falló: sin esto el `change` no vuelve a dispararse.
              e.target.value = '';
            }}
          />
          <button
            className="boton"
            type="button"
            disabled={ocupado}
            onClick={() => archivo.current?.click()}
          >
            {t(foto ? 'photo.replace' : 'photo.choose')}
          </button>
          {foto && (
            <button
              className="boton boton--fantasma"
              type="button"
              disabled={ocupado}
              onClick={() => void cambiar(() => api.removeUserPhoto(usuario.id), t('photo.removed'))}
            >
              {t('photo.remove')}
            </button>
          )}
          <p className="campo__ayuda">{t('photo.hint')}</p>
        </div>
      </div>
    </Modal>
  );
}
